/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useJobService, useJobStream } from './job';
import type { JobMessage } from './job';
import GraphViewer from './appComponents/GraphViewer';
import type { GraphViewerHandle } from './appComponents/GraphViewer';
import ChatPanel from './appComponents/ChatPanel';
import SettingsDrawer from './appComponents/SettingsDrawer';
import HelpDrawer from './appComponents/HelpDrawer';
import { VaultManager } from './appComponents/wiki/VaultManager';
import SidePanel, { type SidePanelTab } from './appComponents/SidePanel';
import { loadAnimationSettings } from './config/animation';
import { normalizeRepoUrl, detectProvider } from '@opentrace/components';
import type { AnimationSettings } from '@opentrace/components';
import { useStore } from './store';
import { ServerGraphStore } from './store/serverStore';
import { GraphDataProvider, useGraph } from './providers/GraphDataProvider';
import { GraphInteractionProvider } from './providers/GraphInteractionProvider';
import { CompileJobProvider } from './providers/CompileJobProvider';
import CompileProgressPanel from './appComponents/CompileProgressPanel';
import './App.css';

interface AppProps {
  version?: string;
  buildTime?: string;
  initialRepoUrl?: string;
  /** Called when the user connects to a remote server via the AddRepo modal. */
  onConnectServer?: (serverUrl: string) => void;
  /** True when the graph store is talking to a backend `opentraceai serve`.
   * Gates features that require the backend (e.g. vault compile + browse). */
  isServerMode?: boolean;
}

function App(props: AppProps = {}) {
  return (
    <GraphDataProvider>
      <GraphInteractionProvider>
        <CompileJobProvider>
          <AppInner {...props} />
        </CompileJobProvider>
      </GraphInteractionProvider>
    </GraphDataProvider>
  );
}

function AppInner({
  version,
  buildTime,
  initialRepoUrl,
  onConnectServer,
  isServerMode = false,
}: AppProps) {
  const { store } = useStore();
  const { loadGraph, startLiveStream, pushLiveBatch, endLiveStream } =
    useGraph();
  const jobService = useJobService();
  const {
    state: jobState,
    start: startJob,
    attach: attachJob,
    cancel: cancelJob,
    minimize: minimizeJob,
    reset: resetJob,
  } = useJobStream(jobService, {
    onGraphDelta: pushLiveBatch,
    onLiveEnd: endLiveStream,
  });

  // Fix #14 — after a page reload, if the agent is still running an
  // index job, attach to it so the user sees the progress indicator
  // resume instead of landing on the empty "Add Repository" state.
  // Server-mode only; ladybug-backed sessions don't have an agent.
  useEffect(() => {
    if (!(store instanceof ServerGraphStore)) return;
    let cancelled = false;
    (async () => {
      try {
        const active = await store.getActiveIndexJob();
        if (cancelled || !active || active.done) return;
        attachJob(active.jobId);
      } catch (err) {
        console.warn('[App] failed to probe for active index job', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only run on mount; attachJob/store identities are stable for a
    // mounted session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const graphViewerRef = useRef<GraphViewerHandle>(null);
  const [chatHighlightNodes, setChatHighlightNodes] = useState<Set<string>>(
    () => new Set(),
  );
  // Ref mirror so the stable stage-click handler can check for active chat
  // highlights without re-creating (and thus re-rendering the memoized
  // GraphViewer) every time the highlight set changes.
  const chatHighlightNodesRef = useRef(chatHighlightNodes);
  chatHighlightNodesRef.current = chatHighlightNodes;
  // Clicking empty canvas drops chat-driven highlights + the traversal trail,
  // so the user no longer has to close the chat or start a new conversation to
  // clear them. No-op when nothing is highlighted (avoids re-allocating an
  // empty Set and re-firing downstream highlight effects on every void click).
  const handleStageDeselect = useCallback(() => {
    if (chatHighlightNodesRef.current.size === 0) return;
    setChatHighlightNodes(new Set());
    graphViewerRef.current?.clearChatTraversal();
  }, []);
  const hasRepoParam = useRef(
    new URLSearchParams(window.location.search).has('repo'),
  );

  const isNewUser = !localStorage.getItem('ot:hasVisited');
  const [showChat, setShowChat] = useState(!isNewUser);
  const [chatWidth, setChatWidth] = useState(480);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(isNewUser);
  const helpWidth = 380;
  const [animationSettings, setAnimationSettings] = useState<AnimationSettings>(
    loadAnimationSettings,
  );
  const [showAddRepo, setShowAddRepo] = useState(false);
  const [showVaults, setShowVaults] = useState(false);
  const [activeRepoUrl, setActiveRepoUrl] = useState('');
  // `true` only when the user explicitly expands the compact LiveIndexingPanel
  // into the full-screen IndexingProgress modal. Defaults `false`: a new job
  // shows the bottom-left panel and the graph builds live behind it, rather
  // than the modal taking over the screen.
  const [jobExpanded, setJobExpanded] = useState(false);
  const [mobilePanelTab, setMobilePanelTab] = useState<SidePanelTab | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleJobSubmit = useCallback(
    (message: JobMessage) => {
      if (message.type === 'connect-server') {
        onConnectServer?.(message.serverUrl);
        return;
      }
      if (message.type === 'index-repo') {
        setActiveRepoUrl(message.repoUrl);
      } else if (message.type === 'index-directory') {
        setActiveRepoUrl(`local/${message.name}`);
      } else if (message.type === 'import-file') {
        setActiveRepoUrl(`import/${message.name}`);
      }
      setShowAddRepo(false);
      setJobExpanded(false);
      const isBrowserPipelineJob =
        !(store instanceof ServerGraphStore) &&
        (message.type === 'index-repo' ||
          message.type === 'reindex-repo' ||
          message.type === 'index-directory');
      if (isBrowserPipelineJob) startLiveStream();
      startJob(message);
    },
    [startJob, onConnectServer, store, startLiveStream],
  );

  // Index or navigate to a repo by URL — shared by ?repo= param and postMessage
  const handleRepoDeepLink = useCallback(
    async (repoUrl: string) => {
      const normalized = normalizeRepoUrl(repoUrl);
      const repos = await store.listNodes('Repository');
      const existing = repos.find((r) => {
        const nodeUrl = r.properties?.url;
        return (
          typeof nodeUrl === 'string' &&
          normalizeRepoUrl(nodeUrl) === normalized
        );
      });

      if (existing) {
        setActiveRepoUrl(repoUrl);
        await loadGraph(existing.id, 2);
        setTimeout(() => graphViewerRef.current?.selectNode(existing.id), 100);
      } else {
        const provider = detectProvider(repoUrl);
        const token = provider
          ? (localStorage.getItem(`ot_${provider}_pat`) ?? undefined)
          : undefined;
        handleJobSubmit({
          type: 'index-repo',
          repoUrl,
          token,
        });
      }
    },
    [store, handleJobSubmit, loadGraph],
  );

  // Handle initial repository on load (from prop or ?repo= param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const repoParam = initialRepoUrl || params.get('repo');
    if (!repoParam) {
      hasRepoParam.current = false;
      return;
    }

    // Clean URL only if repo came from params, and only remove the repo param
    if (!initialRepoUrl && params.has('repo')) {
      params.delete('repo');
      const search = params.toString();
      const url = window.location.pathname + (search ? `?${search}` : '');
      window.history.replaceState({}, '', url);
    }

    handleRepoDeepLink(repoParam).then(() => {
      hasRepoParam.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time mount effect
  }, []);

  // Listen for postMessage from the Chrome extension (reuses existing tab)
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'opentrace:index-repo') return;
      const repoUrl = event.data.repoUrl;
      if (typeof repoUrl === 'string' && repoUrl) {
        handleRepoDeepLink(repoUrl);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleRepoDeepLink]);

  const handleJobClose = useCallback(() => {
    resetJob();
    setJobExpanded(false);
  }, [resetJob]);

  const handleCancelJob = useCallback(() => {
    cancelJob();
    setJobExpanded(false);
  }, [cancelJob]);

  const handleJobMinimize = useCallback(() => {
    minimizeJob();
    setJobExpanded(false);
  }, [minimizeJob]);
  const handleJobExpand = useCallback(() => setJobExpanded(true), []);
  const handleAddRepoOpen = useCallback(() => {
    if (hasRepoParam.current) return; // suppress while deep link is being handled
    // A finished job leaves status at 'done' (the "Complete" bar). The
    // AddRepoModal only renders while status is 'idle', so clear the
    // done job first — otherwise opening it from the toolbar would no-op
    // until the user manually dismisses the completion indicator.
    if (jobState.status === 'done') {
      resetJob();
    }
    setShowAddRepo(true);
  }, [jobState.status, resetJob]);
  const handleAddRepoClose = useCallback(() => setShowAddRepo(false), []);
  const handleToggleChat = useCallback(() => {
    setShowChat((v) => {
      if (!v) {
        setShowSettings(false);
        setShowHelp(false);
        localStorage.setItem('ot:hasVisited', '1');
      }
      return !v;
    });
  }, []);
  const handleChatWidthChange = useCallback((w: number) => setChatWidth(w), []);

  /** On tablet/mobile (≤1024px) with chat open, split vertically: graph on top, chat below */
  const MOBILE_BREAKPOINT = 1024;
  const MOBILE_GRAPH_RATIO = 0.3;
  const isMobile = dimensions.width <= MOBILE_BREAKPOINT;
  const [graphFullscreen, setGraphFullscreen] = useState(false);
  const isMobileSplit = isMobile && (showChat || showHelp) && !graphFullscreen;
  const graphHeight = isMobileSplit
    ? Math.round(dimensions.height * MOBILE_GRAPH_RATIO)
    : dimensions.height;
  const handleToggleGraphFullscreen = useCallback(() => {
    setGraphFullscreen((v) => !v);
    // Wait a frame for the canvas to resize, then fit the graph
    requestAnimationFrame(() => {
      graphViewerRef.current?.resetCamera();
    });
  }, []);
  const handleToggleSettings = useCallback(() => {
    setShowSettings((v) => {
      if (!v) {
        setShowChat(false);
        setShowHelp(false);
        localStorage.setItem('ot:hasVisited', '1');
      }
      return !v;
    });
  }, []);
  const handleToggleHelp = useCallback(() => {
    setShowHelp((v) => {
      if (!v) {
        setShowChat(false);
        setShowSettings(false);
      } else {
        localStorage.setItem('ot:hasVisited', '1');
      }
      return !v;
    });
  }, []);

  return (
    <div className="app" ref={containerRef}>
      <div
        className={`app-body${isMobileSplit ? ' app-body--mobile-split' : ''}`}
      >
        <GraphViewer
          ref={graphViewerRef}
          width={dimensions.width}
          height={graphHeight}
          jobState={jobState}
          activeRepoUrl={activeRepoUrl}
          jobExpanded={jobExpanded}
          onJobClose={handleJobClose}
          onJobCancel={handleCancelJob}
          onJobMinimize={handleJobMinimize}
          onJobExpand={handleJobExpand}
          showAddRepo={showAddRepo}
          onAddRepoOpen={handleAddRepoOpen}
          onAddRepoClose={handleAddRepoClose}
          onJobSubmit={handleJobSubmit}
          showChat={showChat}
          chatWidth={
            isMobileSplit || graphFullscreen
              ? 0
              : showHelp
                ? helpWidth
                : chatWidth
          }
          onToggleChat={handleToggleChat}
          showSettings={showSettings}
          onToggleSettings={handleToggleSettings}
          showHelp={showHelp}
          onToggleHelp={handleToggleHelp}
          chatHighlightNodes={chatHighlightNodes}
          onStageClick={handleStageDeselect}
          animationSettings={animationSettings}
          graphFullscreen={isMobile ? graphFullscreen : undefined}
          onToggleGraphFullscreen={
            isMobile ? handleToggleGraphFullscreen : undefined
          }
          onMobilePanelTabChange={setMobilePanelTab}
          isServerMode={isServerMode}
          showVaults={showVaults}
          onToggleVaults={() => setShowVaults((v) => !v)}
        />

        <SidePanel
          mobileActiveTab={mobilePanelTab}
          onMobileTabChange={setMobilePanelTab}
          onMobileClose={() => setMobilePanelTab(null)}
        />

        {showChat && (
          <div
            style={
              graphFullscreen ? { display: 'none' } : { display: 'contents' }
            }
          >
            <ChatPanel
              onClose={() => setShowChat(false)}
              onNodeSelect={(nodeId) => {
                graphViewerRef.current?.selectNode(nodeId);
              }}
              onChatHighlight={(allIds, newIds) => {
                setChatHighlightNodes(allIds);
                const viewer = graphViewerRef.current;
                if (allIds.size === 0) {
                  // Highlights cleared (turn end / toggle off / convo switch) —
                  // drop the traversal walk + lit trail so the graph un-dims.
                  viewer?.clearChatTraversal();
                } else if (newIds.length > 0) {
                  // Animate the agent walking the graph to the newly-found nodes.
                  viewer?.animateChatTraversal(newIds, allIds);
                }
              }}
              onQuestionSubmit={() => {
                graphViewerRef.current?.zoomToFit();
              }}
              onGraphChange={async (focusNodeId) => {
                // Reload scoped to the PR node — 2 hops shows PR → Files → their neighbors
                await loadGraph(focusNodeId, 2);
                if (focusNodeId) {
                  // Wait for React to commit the re-render with new graph data
                  setTimeout(() => {
                    graphViewerRef.current?.selectNode(focusNodeId, 2);
                  }, 100);
                }
              }}
              repoUrl={activeRepoUrl}
              onWidthChange={handleChatWidthChange}
            />
          </div>
        )}

        {showHelp && (
          <div
            style={
              graphFullscreen ? { display: 'none' } : { display: 'contents' }
            }
          >
            <HelpDrawer
              onClose={() => setShowHelp(false)}
              onOpenAddRepo={() => {
                setShowHelp(false);
                handleAddRepoOpen();
              }}
              onOpenChat={handleToggleChat}
              onOpenSettings={handleToggleSettings}
            />
          </div>
        )}
      </div>

      {showSettings && (
        <SettingsDrawer
          onClose={() => setShowSettings(false)}
          onGraphCleared={() => {
            setShowSettings(false);
            loadGraph();
          }}
          onLimitsChanged={() => loadGraph()}
          onAnimationSettingsChanged={setAnimationSettings}
        />
      )}

      {isServerMode && showVaults && (
        <VaultManager onClose={() => setShowVaults(false)} />
      )}

      {isServerMode && (
        <CompileProgressPanel
          raised={
            !jobExpanded &&
            (jobState.status === 'running' ||
              jobState.status === 'persisted' ||
              jobState.status === 'enriching')
          }
        />
      )}

      {(version || typeof __APP_VERSION__ !== 'undefined') && (
        <footer className="version-footer">
          v{version ?? __APP_VERSION__}
          {(buildTime ??
            (typeof __BUILD_TIME__ !== 'undefined'
              ? __BUILD_TIME__
              : undefined)) && (
            <>
              {' '}
              &middot; {new Date(buildTime ?? __BUILD_TIME__).toLocaleString()}
            </>
          )}
        </footer>
      )}
    </div>
  );
}
export default App;
